import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Avatar, Badge, Button, Empty, Form, Input, Modal, Select, Skeleton, Space, Tag, Typography, message as antdMessage,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import type {
  CreateThreadRequest, GuardianDirectoryRow, ThreadDetail, ThreadListItem,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

/**
 * Shared messaging panel used by admin, teacher and guardian surfaces.
 * When `?thread=<id>` is set we show the detail view; otherwise the list.
 * The server auto-detects the caller (staff vs guardian) so the same panel
 * works everywhere — only the "New thread" button is staff-only.
 */

interface Props {
  /** Container title shown above the list. */
  title?: string;
  /** Show a compact card list style (mobile). Defaults to true on narrow. */
  compact?: boolean;
}

export function MessagesPanel({ title = 'Messages', compact }: Props) {
  const { institution = '' } = useParams<{ institution: string }>();
  const [params, setParams] = useSearchParams();
  const threadId = params.get('thread');
  const [newOpen, setNewOpen] = useState(false);

  const list = useQuery({
    queryKey: ['threads', institution],
    queryFn: () => productApi.listThreads(institution),
    enabled: !!institution,
  });

  const openThread = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('thread', id);
    setParams(next, { replace: true });
  };
  const closeThread = () => {
    const next = new URLSearchParams(params);
    next.delete('thread');
    setParams(next, { replace: true });
  };

  if (list.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (list.isError) {
    const b = list.error instanceof ApiError ? (list.error.body as { error?: string } | null) : null;
    if (b?.error === 'not_linked') {
      return (
        <Alert
          type="warning"
          showIcon
          message="Your account isn't linked to a staff or guardian record yet"
          description="Ask the school to add your email to your record so messaging is enabled."
        />
      );
    }
  }

  const asKind = list.data?.as_kind ?? 'guardian';
  const rows = list.data?.rows ?? [];

  if (threadId) {
    return <ThreadView institution={institution} threadId={threadId} onBack={closeThread} />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{title}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {rows.length} thread{rows.length === 1 ? '' : 's'}
            {list.data && list.data.total_unread > 0 ? ` · ${list.data.total_unread} unread` : ''}
          </Typography.Text>
        </div>
        {asKind === 'staff' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
            New thread
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <Empty
          description={asKind === 'staff'
            ? 'No threads yet — start one with a guardian.'
            : 'No messages from the school yet.'}
          style={{ padding: 20 }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => <ThreadRow key={r.id} row={r} onOpen={() => openThread(r.id)} compact={compact} />)}
        </div>
      )}

      <NewThreadModal
        open={newOpen}
        institution={institution}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => { setNewOpen(false); openThread(id); }}
      />
    </Space>
  );
}

function ThreadRow({ row, onOpen, compact }: {
  row: ThreadListItem; onOpen: () => void; compact?: boolean;
}) {
  const hasUnread = row.unread_count > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        appearance: 'none', border: '1px solid #eef0f3', background: hasUnread ? '#f4f8fd' : '#fff',
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer', textAlign: 'left', width: '100%',
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      <Avatar style={{ background: hasUnread ? '#2f6ab0' : '#c7ccd6', flexShrink: 0 }}>
        {row.counterpart_name.charAt(0).toUpperCase()}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span style={{ fontWeight: hasUnread ? 700 : 600, fontSize: 13.5 }}>{row.counterpart_name}</span>
          {row.student_name && (
            <Tag style={{ margin: 0 }}>
              {row.student_name}{row.student_admission_number ? ` · ${row.student_admission_number}` : ''}
            </Tag>
          )}
          <div style={{ flex: 1 }} />
          {row.last_message_at && (
            <span style={{ fontSize: 11, color: '#8a94a6' }}>{fmtRelative(row.last_message_at)}</span>
          )}
        </div>
        {row.subject && !compact && (
          <div style={{ fontSize: 12.5, color: '#3f4a63', marginTop: 2 }}>{row.subject}</div>
        )}
        {row.last_message_body && (
          <div style={{
            fontSize: 12.5, color: hasUnread ? '#14213d' : '#5b6478',
            marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontWeight: hasUnread ? 500 : 400,
          }}>
            {row.last_message_body}
          </div>
        )}
      </div>
      {hasUnread && (
        <Badge count={row.unread_count} style={{ boxShadow: 'none', flexShrink: 0 }} />
      )}
    </button>
  );
}

function ThreadView({ institution, threadId, onBack }: {
  institution: string; threadId: string; onBack: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['thread', institution, threadId],
    queryFn: () => productApi.getThread(institution, threadId),
    enabled: !!institution && !!threadId,
    refetchInterval: 15_000,
  });
  const send = useMutation({
    mutationFn: (body: string) => productApi.sendMessage(institution, threadId, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['thread', institution, threadId] });
      void qc.invalidateQueries({ queryKey: ['threads', institution] });
      setDraft('');
    },
  });
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [q.data?.messages.length]);
  // Invalidate list so unread counts refresh after opening.
  useEffect(() => {
    if (q.data) void qc.invalidateQueries({ queryKey: ['threads', institution] });
  }, [q.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  const t = q.data;
  const counterpart = t.as_kind === 'staff' ? t.guardian : t.staff;
  const canSend = draft.trim().length > 0 && !send.isPending;

  const sendIt = () => {
    if (!canSend) return;
    send.mutate(draft.trim(), {
      onError: () => void antdMessage.error('Failed to send'),
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} type="text" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{counterpart.name}</div>
          <div style={{ fontSize: 11.5, color: '#8a94a6' }}>
            {t.subject ? t.subject : '(no subject)'}
            {t.student_name ? ` · ${t.student_name}${t.student_admission_number ? ` · ${t.student_admission_number}` : ''}` : ''}
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        style={{
          background: '#f7f7f5', border: '1px solid #eef0f3', borderRadius: 8,
          padding: 12, maxHeight: 520, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {t.messages.map((m) => (
          <div key={m.id} style={{
            alignSelf: m.mine ? 'flex-end' : 'flex-start',
            maxWidth: '78%',
            background: m.mine ? '#2f6ab0' : '#fff',
            color: m.mine ? '#fff' : '#14213d',
            border: m.mine ? 'none' : '1px solid #eef0f3',
            padding: '8px 12px', borderRadius: 10, fontSize: 13.5, whiteSpace: 'pre-wrap',
            boxShadow: m.mine ? 'none' : '0 1px 0 rgba(0,0,0,0.03)',
          }}>
            <div style={{ fontSize: 10.5, opacity: 0.7, marginBottom: 2 }}>
              {m.mine ? 'You' : m.sender_name} · {fmtRelative(m.created_at)}
            </div>
            {m.body}
          </div>
        ))}
        {t.messages.length === 0 && (
          <Empty description="No messages yet" style={{ padding: 12 }} />
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            if (!(e as unknown as { shiftKey?: boolean }).shiftKey) {
              e.preventDefault();
              sendIt();
            }
          }}
          placeholder="Type a reply — Enter to send, Shift+Enter for a new line"
          autoSize={{ minRows: 1, maxRows: 5 }}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={sendIt} disabled={!canSend} loading={send.isPending}>
          Send
        </Button>
      </div>
    </Space>
  );
}

interface NewThreadFormValues {
  guardian_id: string;
  student_id?: string;
  subject?: string;
  body: string;
}

function NewThreadModal({
  open, institution, onClose, onCreated,
}: { open: boolean; institution: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [form] = Form.useForm<NewThreadFormValues>();
  const [search, setSearch] = useState('');
  const guardians = useQuery({
    queryKey: ['guardians-search', institution, search],
    queryFn: () => productApi.listGuardians(institution, search || undefined),
    enabled: open && !!institution,
  });
  const selectedGuardianId = Form.useWatch('guardian_id', form);
  const selectedGuardian = useMemo(
    () => guardians.data?.rows.find((g: GuardianDirectoryRow) => g.id === selectedGuardianId),
    [guardians.data, selectedGuardianId],
  );

  const create = useMutation({
    mutationFn: (input: CreateThreadRequest) => productApi.createThread(institution, input),
    onSuccess: (r) => onCreated(r.id),
  });
  const body = create.error instanceof ApiError ? (create.error.body as { message?: string; error?: string } | null) : null;
  const err = body?.message ?? body?.error ?? (create.error instanceof Error ? create.error.message : null);

  return (
    <Modal
      open={open}
      title="New thread"
      onCancel={() => { create.reset(); onClose(); }}
      okText="Send"
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) =>
          create.mutate({
            guardian_id: v.guardian_id,
            student_id: v.student_id || undefined,
            subject: v.subject?.trim() || undefined,
            body: v.body.trim(),
          })
        }
      >
        <Form.Item name="guardian_id" label="Guardian" rules={[{ required: true, message: 'Pick a guardian' }]}>
          <Select
            showSearch
            filterOption={false}
            onSearch={setSearch}
            placeholder="Type a name or phone"
            loading={guardians.isLoading}
            options={(guardians.data?.rows ?? []).map((g: GuardianDirectoryRow) => ({
              value: g.id,
              label: `${g.name} · ${g.phone}${g.wards[0] ? ` · ${g.wards[0].student_name} (${g.wards[0].admission_number})` : ''}`,
            }))}
          />
        </Form.Item>
        {selectedGuardian && selectedGuardian.wards.length > 0 && (
          <Form.Item name="student_id" label="About which ward? (optional)">
            <Select
              allowClear
              placeholder="Optional — link this thread to one ward"
              options={selectedGuardian.wards.map((w) => ({
                value: w.student_id,
                label: `${w.student_name} · ${w.admission_number} · ${w.grade}${w.section ? `-${w.section}` : ''}`,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item name="subject" label="Subject (optional)">
          <Input placeholder="A short topic — helps the guardian scan their inbox" />
        </Form.Item>
        <Form.Item name="body" label="First message" rules={[{ required: true, min: 1, max: 4000 }]}>
          <Input.TextArea rows={4} placeholder="Type your message…" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={String(err)} />}
      </Form>
    </Modal>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diffSec = (Date.now() - t) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
